<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Checkbox Selection Card Component
      p.text--secondary Individual checkbox buttons wrapped in card containers, designed to work with YzCheckboxGroup for enhanced visual multi-selection.

    // Basic Usage
    .component-section.mb-8#basic-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Usage
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Checkbox Selection Cards
          p.text--secondary.caption Individual checkbox options wrapped in cards for better visual hierarchy with multi-selection support

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .mb-3
            strong Selected: {{ JSON.stringify(basicValue) }}
          yz-checkbox-group(v-model="basicValue")
            .d-flex.flex-column.gap-3
              yz-checkbox-selection-card(value="option1" label="Option 1" description="First choice with basic functionality")
              yz-checkbox-selection-card(value="option2" label="Option 2" description="Second choice with additional features")
              yz-checkbox-selection-card(value="option3" label="Option 3" description="Third choice with premium features")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === basicCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicCode ? 'success' : ''" @click="copyCode(basicCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicCode }}

    // Card Customization
    .component-section.mb-8#card-customization
      h3.mb-4.cursor-pointer(@click="navigateToSection('card-customization')") Card Customization
      .variations-grid
        .variation-item.mb-6(v-for="variation in cardVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            .mb-3
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-checkbox-group(v-model="$data[variation.model]")
              .d-flex.flex-column.gap-3
                yz-checkbox-selection-card(value="a" label="Option A" description="First option" v-bind="variation.props")
                yz-checkbox-selection-card(value="b" label="Option B" description="Second option" v-bind="variation.props")
                yz-checkbox-selection-card(value="c" label="Option C" description="Third option" v-bind="variation.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Layout Variations
    .component-section.mb-8#layout-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('layout-variations')") Layout Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in layoutVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-3
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-checkbox-group(v-model="$data[variation.model]" v-bind="variation.groupProps")
              div(:class="variation.containerClass")
                yz-checkbox-selection-card(value="horizontal-a" label="Option A" description="First choice")
                yz-checkbox-selection-card(value="horizontal-b" label="Option B" description="Second choice")
                yz-checkbox-selection-card(value="horizontal-c" label="Option C" description="Third choice")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Custom Slots
    .component-section.mb-8#custom-slots
      h3.mb-4.cursor-pointer(@click="navigateToSection('custom-slots')") Custom Slots
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('custom-slots')") Label and Description Slots
          p.text--secondary.caption Using custom content for labels and descriptions

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          .mb-3
            strong Selected: {{ JSON.stringify(slotsValue) }}
          yz-checkbox-group(v-model="slotsValue")
            .d-flex.flex-column.gap-3
              yz-checkbox-selection-card(value="feature1")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="success" small) mdi-star
                    span.font-weight-medium Analytics Dashboard
                template(#description)
                  .d-flex.align-center.justify-space-between
                    span Track user engagement and site performance
                    yz-badge(color="green-100" text-color="green-500" size="sm") Popular
              
              yz-checkbox-selection-card(value="feature2")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="primary" small) mdi-crown
                    span.font-weight-medium Advanced Reporting
                template(#description)
                  span Custom reports with advanced filtering and export options
              
              yz-checkbox-selection-card(value="feature3")
                template(#label)
                  .d-flex.align-center
                    v-icon.mr-2(color="warning" small) mdi-diamond-stone
                    span.font-weight-medium Priority Support
                template(#description)
                  span 24/7 dedicated support with guaranteed response times

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === slotsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === slotsCode ? 'success' : ''" @click="copyCode(slotsCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ slotsCode }}

    // Alignment Variations
    .component-section.mb-8#alignment-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('alignment-variations')") Alignment Variations
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('alignment-variations')") Content Alignment Options
          p.text--secondary.caption Different alignment options for checkbox and content

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .mb-3
            strong Selected: {{ JSON.stringify(alignmentValue) }}
          yz-checkbox-group(v-model="alignmentValue")
            .d-flex.flex-column.gap-3
              yz-checkbox-selection-card(value="start" label="Start Alignment (default)" description="Checkbox and content aligned to start/top" align="start")
              yz-checkbox-selection-card(value="center" label="Center Alignment" description="Checkbox and content vertically centered" align="center")
              yz-checkbox-selection-card(value="end" label="End Alignment" description="Checkbox and content aligned to end/bottom" align="end")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === alignmentCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === alignmentCode ? 'success' : ''" @click="copyCode(alignmentCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ alignmentCode }}

    // v-form Validation Examples
    .component-section.mb-8#validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('validation-examples')") Form Validation Integration
          p.text--secondary.caption Checkbox selection cards with comprehensive v-form validation using rules array and required props

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .mb-4
            h5.mb-2 Required validation
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue) }}
            yz-checkbox-group(ref="rulesCheckboxGroup" v-model="rulesCheckboxValue" required requiredErrorMessage="Please select at least one feature")
              .d-flex.flex-column.gap-3
                yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
                yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
                yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetRulesCheckboxGroup")
                yz-btn(label="Validate" @click="validateRulesCheckboxGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="rulesValidationResult" :class="{ 'success--text': rulesValidationResult === 'Valid', 'error--text': rulesValidationResult === 'Invalid' }") {{ rulesValidationResult }}
          
          .mb-4
            h5.mb-2 Combined required + custom validation rules
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue2) }}
            yz-checkbox-group(ref="customRulesCheckboxGroup" v-model="rulesCheckboxValue2" required, :rules="[v => v.length >= 2 || 'Please select at least 2 features']")
              .d-flex.flex-column.gap-3
                yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
                yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
                yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetCustomRulesCheckboxGroup")
                yz-btn(label="Validate" @click="validateCustomRulesCheckboxGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="customRulesValidationResult" :class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}
          
          .mb-4
            h5.mb-2 Required validation using only rules (no required prop)
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue3) }}
            yz-checkbox-group(ref="rulesOnlyCheckboxGroup" v-model="rulesCheckboxValue3" :rules="[v => v.length > 0 || 'Please select at least one feature']")
              .d-flex.flex-column.gap-3
                yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
                yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
                yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")
            .mt-4
              .d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetRulesOnlyCheckboxGroup")
                yz-btn(label="Validate" @click="validateRulesOnlyCheckboxGroup" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="rulesOnlyValidationResult" :class="{ 'success--text': rulesOnlyValidationResult === 'Valid', 'error--text': rulesOnlyValidationResult === 'Invalid' }") {{ rulesOnlyValidationResult }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples
          
          .code-examples
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === requiredCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === requiredCode ? 'success' : ''" @click="copyCode(requiredCode)")
                span.font-weight-medium.ml-2 Required validation
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ requiredCode }}
            
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === customRulesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === customRulesCode ? 'success' : ''" @click="copyCode(customRulesCode)")
                span.font-weight-medium.ml-2 Combined validation rules
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ customRulesCode }}
            
            .mb-3
              .d-flex.align-center.mb-1
                yz-btn(icon-only size="md" :icon="copiedCode === rulesOnlyCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === rulesOnlyCode ? 'success' : ''" @click="copyCode(rulesOnlyCode)")
                span.font-weight-medium.ml-2 Rules-only validation
              pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ rulesOnlyCode }}

    // Smart Padding
    .component-section.mb-8#smart-padding
      h3.mb-4.cursor-pointer(@click="navigateToSection('smart-padding')") Smart Padding
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('smart-padding')") Automatic Padding Detection
          p.text--secondary.caption Component automatically detects parent padding classes and applies them intelligently

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .mb-3
            strong Selected: {{ JSON.stringify(paddingValue) }}
          yz-checkbox-group(v-model="paddingValue")
            .d-flex.flex-column.gap-3
              yz-checkbox-selection-card.pa-2(value="small" label="Small Padding (pa-2)" description="Custom padding applied via class")
              yz-checkbox-selection-card.pa-6(value="large" label="Large Padding (pa-6)" description="Custom padding applied via class")
              yz-checkbox-selection-card.px-8.py-2(value="custom" label="Custom Padding (px-8 py-2)" description="Custom horizontal and vertical padding")
              yz-checkbox-selection-card(value="default" label="Default Padding (pa-4)" description="Default padding when no parent classes detected")

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === paddingCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === paddingCode ? 'success' : ''" @click="copyCode(paddingCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ paddingCode }}

    // Optional Validation Examples
    .component-section.mb-8#optional-validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Feature Cards
          p.text--secondary.caption Optional fields that validate only when selections are made - useful for feature packages, service add-ons, and optional configurations
        
        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          
          // Optional service packages
          .mb-6
            h5.mb-2 Optional service packages
            small.text--secondary Selected: {{ JSON.stringify(optionalPackagesValue) }}
            v-form.mt-2(ref="optionalPackagesForm")
              yz-checkbox-group(v-model="optionalPackagesValue" :rules="optionalPackagesRules")
                .d-flex.flex-column.gap-3
                  yz-checkbox-selection-card(value="premium" label="Premium Package" description="Advanced features with priority support and analytics")
                  yz-checkbox-selection-card(value="business" label="Business Package" description="Team collaboration tools and enterprise integrations")
                  yz-checkbox-selection-card(value="developer" label="Developer Package" description="API access, webhooks, and custom integrations")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalPackagesForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalPackagesForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalPackagesValidationResult" :class="{ 'success--text': optionalPackagesValidationResult === 'Valid', 'error--text': optionalPackagesValidationResult === 'Invalid' }") {{ optionalPackagesValidationResult }}
          
          // Optional add-on services
          .mb-6
            h5.mb-2 Optional add-on services
            small.text--secondary Selected: {{ JSON.stringify(optionalAddonsValue) }}
            v-form.mt-2(ref="optionalAddonsForm")
              yz-checkbox-group(v-model="optionalAddonsValue" :rules="optionalAddonsRules")
                .d-flex.flex-column.gap-3
                  yz-checkbox-selection-card(value="backup" label="Cloud Backup" description="Automated daily backups to secure cloud storage")
                  yz-checkbox-selection-card(value="monitoring" label="Performance Monitoring" description="Real-time system monitoring and alerting")
                  yz-checkbox-selection-card(value="security" label="Security Scanning" description="Automated vulnerability scanning and reporting")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalAddonsForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalAddonsForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalAddonsValidationResult" :class="{ 'success--text': optionalAddonsValidationResult === 'Valid', 'error--text': optionalAddonsValidationResult === 'Invalid' }") {{ optionalAddonsValidationResult }}
          
          // Optional feature toggles
          .mb-6
            h5.mb-2 Optional feature toggles
            small.text--secondary Selected: {{ JSON.stringify(optionalFeaturesValue) }}
            v-form.mt-2(ref="optionalFeaturesForm")
              yz-checkbox-group(v-model="optionalFeaturesValue" :rules="optionalFeaturesRules")
                .d-flex.flex-column.gap-3
                  yz-checkbox-selection-card(value="analytics" label="Advanced Analytics" description="Detailed usage analytics and reporting dashboard")
                  yz-checkbox-selection-card(value="notifications" label="Smart Notifications" description="AI-powered notifications and alerts system")
                  yz-checkbox-selection-card(value="automation" label="Workflow Automation" description="Custom automation rules and triggers")
            
            .d-flex.gap-2.mt-3
              yz-btn(size="sm" color="primary" @click="validateOptionalFeaturesForm") Validate
              yz-btn(size="sm" outlined @click="resetOptionalFeaturesForm") Reset
              span.yz-regular-body-xs.ml-2(v-if="optionalFeaturesValidationResult" :class="{ 'success--text': optionalFeaturesValidationResult === 'Valid', 'error--text': optionalFeaturesValidationResult === 'Invalid' }") {{ optionalFeaturesValidationResult }}
        
        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples
          
          // Optional packages selection code
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalPackagesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalPackagesCode ? 'success' : ''" @click="copyCode(optionalPackagesCode)")
              span.font-weight-medium.ml-2 Optional packages selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalPackagesCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalAddonsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalAddonsCode ? 'success' : ''" @click="copyCode(optionalAddonsCode)")
              span.font-weight-medium.ml-2 Optional addons selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalAddonsCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalFeaturesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalFeaturesCode ? 'success' : ''" @click="copyCode(optionalFeaturesCode)")
              span.font-weight-medium.ml-2 Optional features selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalFeaturesCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // Props
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Events
      h4.mb-3 Events
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
                th Parameters
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}
                td.text--secondary.text-no-wrap {{ event.parameters }}

      // Slots
      h4.mb-3 Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  name: "CheckboxSelectionCard",
  data() {
    return {
      copiedCode: null,
      basicValue: ['option1'],
      borderValue: ['a'],
      colorValue: ['a'],
      radiusValue: ['a'],
      disabledValue: ['a'],
      horizontalValue: ['horizontal-a'],
      spacingValue: ['horizontal-a'],
      slotsValue: ['feature2'],
      alignmentValue: ['start'],
      paddingValue: ['default'],
      invalidValue: [],
      rulesCheckboxValue: [],
      rulesCheckboxValue2: [],
      rulesCheckboxValue3: [],
      rulesValidationResult: null,
      customRulesValidationResult: null,
      rulesOnlyValidationResult: null,
      
      // Optional Validation Examples Data
      optionalPackagesValue: [],
      optionalAddonsValue: [],
      optionalFeaturesValue: [],
      optionalPackagesValidationResult: null,
      optionalAddonsValidationResult: null,
      optionalFeaturesValidationResult: null,
      basicCode: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="option1" label="Option 1" description="First choice with basic functionality")
    yz-checkbox-selection-card(value="option2" label="Option 2" description="Second choice with additional features")
    yz-checkbox-selection-card(value="option3" label="Option 3" description="Third choice with premium features")`,
      slotsCode: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="feature1")
      template(#label)
        .d-flex.align-center
          v-icon.mr-2(color="success" small) mdi-star
          span.font-weight-medium Analytics Dashboard
      template(#description)
        .d-flex.align-center.justify-space-between
          span Track user engagement and site performance
          yz-badge(color="green-100" text-color="green-500" size="sm") Popular`,
      alignmentCode: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="start" label="Start Alignment (default)" description="Checkbox and content aligned to start/top" align="start")
    yz-checkbox-selection-card(value="center" label="Center Alignment" description="Checkbox and content vertically centered" align="center")
    yz-checkbox-selection-card(value="end" label="End Alignment" description="Checkbox and content aligned to end/bottom" align="end")`,
      paddingCode: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    // Custom small padding
    yz-checkbox-selection-card.pa-2(value="small" label="Small Padding (pa-2)" description="Custom padding applied via class")
    
    // Custom large padding
    yz-checkbox-selection-card.pa-6(value="large" label="Large Padding (pa-6)" description="Custom padding applied via class")
    
    // Custom directional padding
    yz-checkbox-selection-card.px-8.py-2(value="custom" label="Custom Padding (px-8 py-2)" description="Custom horizontal and vertical padding")
    
    // Default padding (pa-4) when no parent classes
    yz-checkbox-selection-card(value="default" label="Default Padding (pa-4)" description="Default padding when no parent classes detected")`,
      requiredCode: `yz-checkbox-group(v-model="selectedValues" required required-error-message="Please select at least one feature")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
    yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
    yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")`,
      customRulesCode: `yz-checkbox-group(v-model="selectedValues" required :rules="[v => v.length >= 2 || 'Please select at least 2 features']")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
    yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
    yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")`,
      rulesOnlyCode: `yz-checkbox-group(v-model="selectedValues" :rules="[v => v.length > 0 || 'Please select at least one feature']")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="analytics" label="Analytics Dashboard" description="Track user engagement and site performance")
    yz-checkbox-selection-card(value="reporting" label="Advanced Reporting" description="Custom reports with filtering and export")
    yz-checkbox-selection-card(value="support" label="Priority Support" description="24/7 dedicated support with guaranteed response")`,
      cardVariations: [
        {
          name: 'Custom Border',
          id: 'custom-border',
          description: 'Cards with custom border and background colors',
          model: 'borderValue',
          props: {
            borderColor: 'yz-blue-500',
            color: 'yz-blue-100'
          },
          code: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="a" label="Option A" description="First option" border-color="yz-blue-500" color="yz-blue-100")
    yz-checkbox-selection-card(value="b" label="Option B" description="Second option" border-color="yz-blue-500" color="yz-blue-100")
    yz-checkbox-selection-card(value="c" label="Option C" description="Third option" border-color="yz-blue-500" color="yz-blue-100")`
        },
        {
          name: 'Custom Background',
          id: 'custom-background',
          description: 'Cards with custom background color',
          model: 'colorValue',
          props: {
            color: 'blue-100',
            selectedColor: 'neutral-400'
          },
          code: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="a" label="Option A" description="First option" color="blue-100" selected-color="neutral-400")
    yz-checkbox-selection-card(value="b" label="Option B" description="Second option" color="blue-100" selected-color="neutral-400")
    yz-checkbox-selection-card(value="c" label="Option C" description="Third option" color="blue-100" selected-color="neutral-400")`
        },
        {
          name: 'Custom Radius',
          id: 'custom-radius',
          description: 'Cards with custom border radius',
          model: 'radiusValue',
          props: {
            radius: '20px'
          },
          code: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="a" label="Option A" description="First option" radius="20px")
    yz-checkbox-selection-card(value="b" label="Option B" description="Second option" radius="20px")
    yz-checkbox-selection-card(value="c" label="Option C" description="Third option" radius="20px")`
        },
        {
          name: 'Disabled State',
          id: 'disabled-state',
          description: 'Disabled checkbox selection cards',
          model: 'disabledValue',
          props: {
            disabled: true
          },
          code: `yz-checkbox-group(v-model="selectedValues")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="a" label="Option A" description="First option" disabled)
    yz-checkbox-selection-card(value="b" label="Option B" description="Second option" disabled)
    yz-checkbox-selection-card(value="c" label="Option C" description="Third option" disabled)`
        }
      ],
      layoutVariations: [
        {
          name: 'Horizontal Layout',
          id: 'horizontal-layout',
          description: 'Checkbox cards arranged horizontally',
          model: 'horizontalValue',
          groupProps: {
            direction: 'horizontal'
          },
          containerClass: 'd-flex gap-3',
          code: `yz-checkbox-group(v-model="selectedValues" direction="horizontal")
  .d-flex.gap-3
    yz-checkbox-selection-card(value="horizontal-a" label="Option A" description="First choice")
    yz-checkbox-selection-card(value="horizontal-b" label="Option B" description="Second choice")
    yz-checkbox-selection-card(value="horizontal-c" label="Option C" description="Third choice")`
        },
        {
          name: 'Custom Spacing',
          id: 'custom-spacing',
          description: 'Checkbox cards with custom spacing',
          model: 'spacingValue',
          groupProps: {
            direction: 'horizontal',
            spacing: '32px'
          },
          containerClass: 'd-flex gap-8',
          code: `yz-checkbox-group(v-model="selectedValues" direction="horizontal" spacing="32px")
  .d-flex.gap-8
    yz-checkbox-selection-card(value="horizontal-a" label="Option A" description="First choice")
    yz-checkbox-selection-card(value="horizontal-b" label="Option B" description="Second choice")
    yz-checkbox-selection-card(value="horizontal-c" label="Option C" description="Third choice")`
        }
      ],
      componentProps: [
        {
          name: 'value',
          type: 'Any',
          default: '-',
          description: 'The value of this checkbox option (required)'
        },
        {
          name: 'label',
          type: 'String',
          default: '',
          description: 'Label text for the checkbox card'
        },
        {
          name: 'description',
          type: 'String',
          default: '',
          description: 'Description text displayed below the label'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the checkbox card is disabled'
        },
        {
          name: 'align',
          type: 'String',
          default: 'start',
          description: 'Vertical alignment of checkbox and content: "start", "center", or "end"'
        },
        {
          name: 'radius',
          type: 'String|Number',
          default: '10px',
          description: 'Border radius of the card'
        },
        {
          name: 'color',
          type: 'String',
          default: 'white',
          description: 'Background color of the card when not selected'
        },
        {
          name: 'selectedColor',
          type: 'String',
          default: 'neutral-200',
          description: 'Background color of the card when selected'
        },
        {
          name: 'borderColor',
          type: 'String',
          default: 'neutral-300',
          description: 'Border color of the card when not selected'
        },
        {
          name: 'selectedBorderColor',
          type: 'String',
          default: 'neutral-700',
          description: 'Border color of the card when selected'
        },
        {
          name: 'borderWidth',
          type: 'String|Number',
          default: '1',
          description: 'Border width of the card when not selected'
        },
        {
          name: 'selectedBorderWidth',
          type: 'String|Number',
          default: '2',
          description: 'Border width of the card when selected'
        },
        {
          name: 'borderStyle',
          type: 'String',
          default: 'solid',
          description: 'Border style of the card: "solid", "dashed", or "dotted"'
        },
        {
          name: 'hoverBackgroundColor',
          type: 'String',
          default: 'neutral-200',
          description: 'Background color of the card on hover. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500)'
        },
        {
          name: 'hoverBorderColor',
          type: 'String',
          default: 'neutral-500',
          description: 'Border color of the card on hover. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500)'
        },
        {
          name: 'invalid',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the checkbox card should show error state styling. Automatically inherits from parent checkbox group validation.'
        }
      ],
      componentEvents: [
        {
          name: 'input',
          description: 'Emitted when this checkbox card is toggled (standalone use)',
          parameters: 'value: Any'
        },
        {
          name: 'change',
          description: 'Emitted when this checkbox card is toggled',
          parameters: 'value: Any'
        },
        {
          name: 'focus',
          description: 'Emitted when the checkbox card receives focus',
          parameters: 'event: FocusEvent'
        },
        {
          name: 'blur',
          description: 'Emitted when the checkbox card loses focus',
          parameters: 'event: FocusEvent'
        }
      ],
      componentSlots: [
        {
          name: 'label',
          description: 'Custom content for the label area'
        },
        {
          name: 'description',
          description: 'Custom content for the description area'
        }
      ]
    }
  },
  computed: {
    // Optional Validation Rules
    optionalPackagesRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['premium', 'business', 'developer'].includes(item))) || 'Please select valid service packages'
      ]
    },
    optionalAddonsRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['backup', 'monitoring', 'security'].includes(item))) || 'Please select valid addon services'
      ]
    },
    optionalFeaturesRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['analytics', 'notifications', 'automation'].includes(item))) || 'Please select valid feature options'
      ]
    },
    
    // Code examples for optional validation
    optionalPackagesCode() {
      return `yz-checkbox-group(v-model="servicePackages" :rules="optionalPackagesRules")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="premium" label="Premium Package" description="Advanced features with priority support and analytics")
    yz-checkbox-selection-card(value="business" label="Business Package" description="Team collaboration tools and enterprise integrations")
    yz-checkbox-selection-card(value="developer" label="Developer Package" description="API access, webhooks, and custom integrations")

// Validation rule
optionalPackagesRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['premium', 'business', 'developer'].includes(item))) || 'Please select valid service packages'
  ]
}`
    },
    optionalAddonsCode() {
      return `yz-checkbox-group(v-model="addonServices" :rules="optionalAddonsRules")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="backup" label="Cloud Backup" description="Automated daily backups to secure cloud storage")
    yz-checkbox-selection-card(value="monitoring" label="Performance Monitoring" description="Real-time system monitoring and alerting")
    yz-checkbox-selection-card(value="security" label="Security Scanning" description="Automated vulnerability scanning and reporting")

// Validation rule
optionalAddonsRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['backup', 'monitoring', 'security'].includes(item))) || 'Please select valid addon services'
  ]
}`
    },
    optionalFeaturesCode() {
      return `yz-checkbox-group(v-model="featureToggles" :rules="optionalFeaturesRules")
  .d-flex.flex-column.gap-3
    yz-checkbox-selection-card(value="analytics" label="Advanced Analytics" description="Detailed usage analytics and reporting dashboard")
    yz-checkbox-selection-card(value="notifications" label="Smart Notifications" description="AI-powered notifications and alerts system")
    yz-checkbox-selection-card(value="automation" label="Workflow Automation" description="Custom automation rules and triggers")

// Validation rule
optionalFeaturesRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['analytics', 'notifications', 'automation'].includes(item))) || 'Please select valid feature options'
  ]
}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    validateRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.rulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateCustomRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.customRulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.customRulesValidationResult = null;
          }, 1500);
        }
      });
    },
    validateRulesOnlyCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesOnlyCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.rulesOnlyValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesOnlyValidationResult = null;
          }, 1500);
        }
      });
    },
    resetRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.rulesValidationResult = null;
          this.rulesCheckboxValue = [];
        }
      });
    },
    resetCustomRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.customRulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.customRulesValidationResult = null;
          this.rulesCheckboxValue2 = [];
        }
      });
    },
    resetRulesOnlyCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesOnlyCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.rulesOnlyValidationResult = null;
          this.rulesCheckboxValue3 = [];
        }
      });
    },
    
    // Optional Validation Methods
    validateOptionalPackagesForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalPackagesForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalPackagesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalPackagesValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalPackagesForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalPackagesForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalPackagesValidationResult = null;
          this.optionalPackagesValue = [];
        }
      });
    },
    validateOptionalAddonsForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalAddonsForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalAddonsValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalAddonsValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalAddonsForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalAddonsForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalAddonsValidationResult = null;
          this.optionalAddonsValue = [];
        }
      });
    },
    validateOptionalFeaturesForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalFeaturesForm;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalFeaturesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalFeaturesValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalFeaturesForm() {
      this.$nextTick(() => {
        const formRef = this.$refs.optionalFeaturesForm;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalFeaturesValidationResult = null;
          this.optionalFeaturesValue = [];
        }
      });
    },
    
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: transparent;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>